import { openProfileDocument } from "../../../../backend/transport";
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useSearchParams } from "../../../../backend/navigation";
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../../common';
import { PRODUCTION_COLUMN, ROUTES, TITLES, BUTTON, LOCAL_STORAGE, FABRIC_PRODUCTION_DETAILS_COLUMN } from '../../../../constants';
import { Button, Input, PopUpBox, ThresholdWarningPopup } from '../../../shared';
import { vendorManufacturing, exportBulkManfacture, exportBulkManfactureTemplate, importBulkManfacture, saveAsDraftManufacturing, validateBulkManufacturing } from '../../../../service';
import { useDebounce } from '../../../../hooks';
import { fabricManufacturingProductCompiler, dispatchDataCompiler, getLocalStorageItem, setLocalStorageItem } from '../../../../utils';
import ImportModalContent from './ImportModalContent';
import { popup } from '../../../../utils';
import { vendorReceipt } from '../../../../service/receipt';
import { InputWithSearch } from '../../../shared';
import { getGroupBy } from '../../../../service';
import { GROUP_BY } from '../../../../constants';
import { useNodeSocket } from '../../../../context/NodeSocketContext';

// Safety net for bulk imports/validations whose Odoo queue job dies without ever emitting
// a completion event over the WebSocket. Without this, a picking spins on "Started"
// indefinitely (see the bulk-validate crash in DEV-2892). If no in-progress snapshot has
// referenced a picking for longer than this window, we treat it as stalled, clear the
// stale indicator, and re-sync with the server. Tune the timeout up if large legitimate
// imports are flagged early.
const STUCK_PICKING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const STUCK_PICKING_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds


// Called inside effects: consume each deliberate event once, using its current closure.
// Reset on unmount also allows React StrictMode to replay mount setup safely.
const consumeListTrigger = (ref, key, next) => {
  const previous = ref.current[key];
  if (previous && next.every((value, i) => Object.is(value, previous[i]))) return false;
  ref.current[key] = next;
  return true;
};

export const ProductionContent = () => {
  // Only explicit list events issue queries; draft text and response metadata do not.
  const listTriggers = useRef({});
  useEffect(() => () => {
    listTriggers.current = {};
    isInitialLoad.current = true;
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialLoad = useRef(true);

  // Initialize state from URL params or default values
  const [ProductionData, setProductionData] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('filter') || PRODUCTION_COLUMN[12].name);
  const [readyStatus, setReadyStatus] = useState(searchParams.get('readyStatus') || 'Ready Status');
  const [rightTableColumn, setRightTableColumn] = useState([]);
  const [sortingArray1, setSortingArray1] = useState([]);
  // const [readyStatusArray] = useState(['Not Ready', 'Partially Ready', 'Fully Ready']);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [bulkMode, setBulkMode] = useState(searchParams.get('bulkMode') === 'true' ? 1 : 0);
  const [filteredColumn, setFilteredColumn] = useState(PRODUCTION_COLUMN);
  const [moList, setMoList] = useState([]);
  const [moImport, setMoImport] = useState(false);
  const [fullProductData, setFullProductData] = useState([]);
  const [showRightTable, setShowRightTable] = useState(false)
  const [activeRequests, setActiveRequests] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [updatedMoves, setUpdatedMoves] = useState({});
  const [isSaveAsDraftLoading, setIsSaveAsDraftLoading] = useState(false);
  const [activeValidateRequests, setActiveValidateRequests] = useState(0);
  const isValidateLoading = activeValidateRequests > 0;
  const rightTableLoading = activeRequests > 0;
  const [importFileLoading, setImportFileLoading] = useState(false)
  const [inProgressPickings, setInProgressPickings] = useState([]);
  const [inProgressIds, setInProgressIds] = useState([]);
  const [pickingStatus, setPickingStatus] = useState({});
  const [containerNo, setContainerNo] = useState('');
  const { messages } = useNodeSocket();
  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [selectableRecords, setSelectableRecords] = useState(null);
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const bulkRequestSeqRef = useRef(0);
  // Maps picking id -> last timestamp the WebSocket reported it as in-progress.
  const lastSeenInProgressRef = useRef({});
  // Always points at the latest getProductionList so the mount-once watchdog interval
  // never calls a stale closure.
  const getProductionListRef = useRef(null);
  const [groupArray, setGroupArray] = useState([]);
  const [groupBy, setGroupBy] = useState(searchParams.get('groupBy') || GROUP_BY.TITLE);
  const [availableValuesGroupBy, setAvailableValuesGroupBy] = useState([]);
  const [selectedItem, setSelectedItem] = useState(
    searchParams.get('groupItems') ? searchParams.get('groupItems').split(',') : [],
  );
  const [groupApplied, setGroupApplied] = useState(searchParams.get('groupApplied') === 'true');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [isExportCSVLoading, setIsExportCSVLoading] = useState(false);
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null);
  const [pendingImportData, setPendingImportData] = useState(null);

  const AVAILABLE_GROUP_VALUES_KEY = 'production_available_group_values';

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'restoreCachedGroups', [])) return;
    try {
      // Try to restore available values from localStorage
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      const currentGroupBy = searchParams.get('groupBy') || GROUP_BY.TITLE;

      if (cachedValues[currentGroupBy] && Array.isArray(cachedValues[currentGroupBy])) {
        setAvailableValuesGroupBy(cachedValues[currentGroupBy]);
      }
    } catch (error) {
      console.error('Error loading cached group values:', error);
    }
  }, [searchParams]);





  useEffect(() => {
    // Skip the initial setting when component first mounts
    if (!isRestoringSession && availableValuesGroupBy.length > 0) {
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};

        // Update the cached values for the current groupBy
        const updatedCache = {
          ...cachedValues,
          [groupBy]: availableValuesGroupBy,
        };

        setLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY, updatedCache);
      } catch (error) {
        console.error('Error caching group values:', error);
      }
    }

    // After first render, mark restoration as complete
    if (isRestoringSession) {
      setIsRestoringSession(false);
    }
  }, [availableValuesGroupBy, groupBy, isRestoringSession]);



  // Keep the watchdog's reference to getProductionList current on every render.
  useEffect(() => {
    getProductionListRef.current = getProductionList;
  });

  // Refresh liveness for every picking the latest WebSocket snapshot still reports as
  // in-progress, and drop pickings that have resolved. This fires on each snapshot
  // (inProgressIds is set to a fresh array per message), so a healthy long-running job
  // keeps refreshing its timestamp; a crashed job stops emitting and goes stale.
  useEffect(() => {
    const now = Date.now();
    const seen = lastSeenInProgressRef.current;
    const active = new Set(inProgressIds.map((id) => String(id)));

    active.forEach((id) => {
      seen[id] = now;
    });

    Object.keys(seen).forEach((id) => {
      if (!active.has(id)) delete seen[id];
    });
  }, [inProgressIds]);

  // Watchdog: if a picking has been reported in-progress but no snapshot has touched it
  // for longer than STUCK_PICKING_TIMEOUT_MS, assume its server-side job died, clear the
  // stale spinner/status so the UI is no longer frozen on "Started", and re-sync.
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = Date.now();
      const seen = lastSeenInProgressRef.current;
      const stuckIds = Object.keys(seen).filter(
        (id) => now - seen[id] > STUCK_PICKING_TIMEOUT_MS,
      );

      if (stuckIds.length === 0) return;

      const stuckSet = new Set(stuckIds);
      stuckIds.forEach((id) => delete seen[id]);

      setInProgressIds((prev) => prev.filter((id) => !stuckSet.has(String(id))));
      setPickingStatus((prev) => {
        const next = { ...prev };
        stuckIds.forEach((id) => delete next[id]);
        return next;
      });

      popup(
        'error',
        'A manufacturing import is taking longer than expected and may have failed on the server. The list has been refreshed — please verify the records and retry, or contact support if this keeps happening.',
      );

      getProductionListRef.current?.({});
    }, STUCK_PICKING_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

    const filteredColumns = FABRIC_PRODUCTION_DETAILS_COLUMN.filter((col) => {
      const name = col.name;

      // Always remove 'Action'
      if (name === 'Action') return false;

      // Remove these if vendor is NOT 'fabric'
      const fabricOnly = ['Rolls', 'Color', 'Size', 'Produced Quantity', 'Damaged Quantity'];
      if (fabricOnly.includes(name) && vendorType !== 'fabric') return false;

      // Remove these if vendor is NOT 'dyeing'
      const dyeingOnly = ['Ready Status', 'Produced Quantity', 'Damaged Quantity'];
      if (dyeingOnly.includes(name) && vendorType !== 'dyeing') return false;

      return true;
    });

    setRightTableColumn(filteredColumns);
  }, []);

  // Update URL params when values change
  const updateUrlParams = useCallback((params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    if (params.filter && params.filter !== PRODUCTION_COLUMN[12].name) newParams.filter = params.filter;
    if (params.readyStatus && params.readyStatus !== 'Ready Status') newParams.readyStatus = params.readyStatus;
    if (params.page) newParams.page = params.page.toString();
    if (params.groupBy && params.groupBy !== GROUP_BY.TITLE) newParams.groupBy = params.groupBy;

    // Fix for groupItems - extract only IDs before joining them in URL
    if (params.groupItems && params.groupItems.length > 0) {
      const ids = params.groupItems.map((item) => {
        // If item is an array [id, name], extract only the ID
        if (Array.isArray(item) && item.length > 1) {
          return item[0];
        }
        // If it's already a string ID or simple value, use as is
        return item;
      });
      newParams.groupItems = ids.join(',');
    }

    if (params.groupApplied === true) newParams.groupApplied = 'true';

    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  const fetchGroupBY = useDebounce((params = {}) => {
    getGroupBy({ data: params })
      .then((response) => {
        if (response?.data && response?.data[groupBy]) {
          setAvailableValuesGroupBy(response?.data[groupBy]);
        }
      })
      .catch((error) => {
        console.error('Error fetching group by options:', error);
        // If the API fails, try to use cached values
        try {
          const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
          if (cachedValues[groupBy] && Array.isArray(cachedValues[groupBy])) {
            setAvailableValuesGroupBy(cachedValues[groupBy]);
          }
        } catch (err) {
          console.error('Error using backup cached values:', err);
        }
      })
  }, 400);

  const refreshGroupByOptions = useCallback((includeFilters = true) => {
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      let params = {
        related_to: GROUP_BY.MANUFACTURING,
        field_name: groupBy,
      };

      // Only include search and sort if requested
      if (includeFilters) {
        if (searchTerm) {
          params.search = {
            name: searchTerm,
            origin: searchTerm,
            partner_ref: searchTerm,
            'move_ids.future_lot': searchTerm,
            finished_products: searchTerm,
            physical_container_no: searchTerm,
            lot_color: searchTerm,
            rib_color: searchTerm
          };
        }

        let sortSelection = {};
        if (sortingArray1.includes(sort1)) {
          sortSelection.state = sort1?.toString().toLowerCase();
        }
        if (readyStatus !== 'Ready Status') {
          sortSelection.ready_status = readyStatus?.toString().toLowerCase().replaceAll(' ', '_');
        }

        if (Object.keys(sortSelection).length > 0) {
          params.sort = { selection: sortSelection };
        }
      }
      fetchGroupBY(params);
    }
  }, [fetchGroupBY, groupArray, groupBy, initialLoadComplete, readyStatus, searchTerm, sort1, sortingArray1]);

  const shouldApplyGroupBy = useCallback(() => {
    // Only apply groupBy if:
    // 1. groupApplied flag is true
    // 2. groupBy is a valid option in groupArray
    // 3. At least one item is selected OR we're just applying the groupBy field
    return (
      groupApplied &&
      ((groupArray.length > 0 && groupArray.includes(groupBy)) || initialLoadComplete === false) &&
      (selectedItem.length > 0 || !searchParams.get('groupItems'))
    );
  }, [groupApplied, groupArray, groupBy, initialLoadComplete, searchParams, selectedItem.length]);

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

    if (!bulkMode) {
      setMoList([]);
    }

    let columns = bulkMode
      ? PRODUCTION_COLUMN
      : PRODUCTION_COLUMN.filter((col) => col.name !== '*');

    const restrictedFields = ['Priority', 'Lot Size', 'Lot Color', 'Rib Size', 'Rib Color'];

    columns = columns.filter((col) => {
      if (col.name === 'Finished Product' && vendorType !== 'clothing' && vendorType !== 'dyeing') {
        return false;
      }
      if (restrictedFields.includes(col.name) && vendorType !== 'fabric') {
        return false;
      }
      if (col.name === 'Backorder Of' && vendorType === 'fabric') {
        return false;
      }
      if (col.name === 'Lot No' && vendorType === 'dyeing') {
        return false;
      }
      return true;
    });

    setFilteredColumn(columns);
  }, [bulkMode]);

  const fetchProductionData = useDebounce(async (params = {}) => {
    setIsLoading(true);

    vendorManufacturing({ data: params })
      .then(async (response) => {
        const records = response?.data?.records || [];
        const pagination = response?.data?.params || {};
        const name = PRODUCTION_COLUMN[12].dataField[1];
        const sortingArray = Array.isArray(response?.data[name]) ? response.data[name] : [];

        setProductionData(records);
        setPaginationData(pagination);
        setSortingArray1(sortingArray);
        setSelectableRecords(response?.data?.selectable_records || 0);
        setGroupArray(response?.data?.groupby || []); // Add this line for groupBy options
      })
      .catch((error) => console.error('Error fetching production data:', error))
      .finally(() => {
        setIsLoading(false);
      });
  }, 400);

  const getProductionList = useCallback((additionalParams = {}) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");

    const shouldUseGroupBy = shouldApplyGroupBy();

    let sortSelection = {};
    if (sortingArray1.includes(sort1)) sortSelection.state = sort1?.toString().toLowerCase();
    if (readyStatus !== 'Ready Status') sortSelection.ready_status = readyStatus?.toString().toLowerCase().replaceAll(' ', '_');

    const params = {
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
          name: searchTerm,
          origin: searchTerm,
          partner_ref: searchTerm,
          'move_ids.future_lot': searchTerm,
          finished_products: searchTerm,
          physical_container_no: searchTerm,
          lot_color: searchTerm,
          rib_color: searchTerm
        }
        : {},
      sort: { selection: Object.keys(sortSelection).length > 0 ? sortSelection : {} },
      groupby_field: shouldUseGroupBy ? groupBy : '',
      manufacturing: true, // Manufacturing page - enable groupby and move_ids
      ...additionalParams,
    };

    if (shouldUseGroupBy && selectedItem.length) {
      if (groupBy.includes(',')) {
        const fields = groupBy.split(',');
        const groupbySpecifics = {};
        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          );
        });
        params.groupby_specifics = groupbySpecifics;
      } else {
        params.groupby_specifics = {
          [groupBy]: selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          ),
        };
      }
    } else {
      params.groupby_specifics = {};
    }

    updateUrlParams({
      search: searchTerm,
      filter: sortingArray1.includes(sort1) ? sort1 : '',
      readyStatus: readyStatus !== 'Ready Status' ? readyStatus : '',
      page: params.page_number,
      groupBy: shouldUseGroupBy ? groupBy : '',
      groupItems: shouldUseGroupBy && selectedItem.length ? selectedItem : [],
      groupApplied: shouldUseGroupBy,
    });

    fetchProductionData(params);
  }, [fetchProductionData, groupBy, pageNumber, readyStatus, searchTerm, selectedItem, shouldApplyGroupBy, sort1, sortingArray1, updateUrlParams]);

  const removeGroupBy = () => {
    setGroupApplied(false);
    setSelectedItem([]);
    setAvailableValuesGroupBy([]);
    setGroupBy(GROUP_BY.TITLE);

    // Update URL - remove group params but keep existing filters
    updateUrlParams({
      search: searchTerm,
      filter: sortingArray1.includes(sort1) ? sort1 : '',
      readyStatus: readyStatus !== 'Ready Status' ? readyStatus : '',
      page: 1,
      // Explicitly omit groupBy, groupItems, and groupApplied
    });

    // Fetch data with groupBy removed
    getProductionList({
      page_number: 1,
      groupby_field: '',
      groupby_specifics: {},
    });
  };

  const handleGroupByChange = (newGroupBy) => {
    if (newGroupBy === groupBy) return;

    setGroupBy(newGroupBy);
    // When changing the groupBy field, reset selected items
    setSelectedItem([]);

    // Apply the groupBy automatically when it's a valid option
    if (groupArray.includes(newGroupBy)) {
      setGroupApplied(true);

      // Try to load cached values first for a smoother experience
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
        if (cachedValues[newGroupBy] && Array.isArray(cachedValues[newGroupBy])) {
          setAvailableValuesGroupBy(cachedValues[newGroupBy]);
        }
      } catch (err) {
        console.error('Error using cached values for new groupBy:', err);
      }
    }
  };

useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'selectedItem', [selectedItem])) return;
    // Skip initial render
    if (initialLoadComplete) {
      // Apply groupBy automatically when items are selected
      setGroupApplied(true);

      // Reset to page 1 only when first applying the groupBy
      // (when there were no items before but now there are)
      const prevItems = searchParams.get('groupItems');
      const wasEmpty = !prevItems || prevItems.split(',').length === 0;

      if (wasEmpty) {
        setPageNumber(1);
        getProductionList({ page_number: 1 });
      } else {
        // Keep the current page number for subsequent selections
        getProductionList({ page_number: pageNumber });
      }
    }
  }, [selectedItem, getProductionList, initialLoadComplete, pageNumber, searchParams]);

useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'groupBy', [groupBy])) return;
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      // When changing the groupBy field, we want to show all options
      // regardless of current filters
      refreshGroupByOptions(false); // Don't include filters for initial groupBy options
    }
  }, [groupBy, groupArray, initialLoadComplete, refreshGroupByOptions]);

useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'groupApplied', [groupApplied])) return;
    if (initialLoadComplete) {
      // Keep the current page number when groupApplied changes
      getProductionList({ page_number: 1 });
    }
  }, [groupApplied, getProductionList, initialLoadComplete]);

useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'messages', [messages])) return;
    if (!messages.length) return;

    const latestMessage = messages[messages.length - 1];

    const currentVendorType = (getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) || "")
      .replace(/"/g, "")
      .trim()
      .toLowerCase();

    const msgVendorType = latestMessage.data?.type?.toString().trim().toLowerCase();

    if (msgVendorType === currentVendorType) {
      const payload = latestMessage.data?.result; // Note: your Odoo code sends 'result', not 'payload'

      if (payload?.in_progress_lots === 0) {
        getProductionList();
      }

      setInProgressIds([...new Set(payload?.in_progress_ids || [])]);

      setPickingStatus((prev) => ({
        ...prev,
        ...(payload?.pickings_state || {}),
      }));
    }
  }, [messages, getProductionList]);

  // Initial load - read from URL params
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'restoreInitialQuery', [])) return;
    if (isInitialLoad.current) {
      // On initial load, if we have params in the URL, use them
      const urlSearchTerm = searchParams.get('search');
      const urlFilter = searchParams.get('filter');
      const urlReadyStatus = searchParams.get('readyStatus');
      const urlPage = searchParams.get('page');
      const urlGroupBy = searchParams.get('groupBy');
      const urlGroupItems = searchParams.get('groupItems');
      const urlGroupApplied = searchParams.get('groupApplied');

      let hasChanges = false;
      let hasGroupByParams = false;

      if (urlSearchTerm && urlSearchTerm !== searchTerm) {
        setSearchTerm(urlSearchTerm);
        hasChanges = true;
      }

      if (urlFilter && urlFilter !== sort1) {
        setSort1(urlFilter);
        hasChanges = true;
      }

      if (urlReadyStatus && urlReadyStatus !== readyStatus) {
        setReadyStatus(urlReadyStatus);
        hasChanges = true;
      }

      if (urlPage && parseInt(urlPage) !== pageNumber) {
        setPageNumber(parseInt(urlPage));
        hasChanges = true;
      }

      if (urlGroupBy && urlGroupBy !== groupBy) {
        setGroupBy(urlGroupBy);
        hasGroupByParams = true;
      }

      if (urlGroupItems) {
        const items = urlGroupItems.split(',');

        // Find the corresponding full items from availableValuesGroupBy if possible
        const fullItems = items.map((id) => {
          // Try to find a matching item in availableValuesGroupBy
          const match = availableValuesGroupBy.find(
            (item) => Array.isArray(item) && item[0].toString() === id.toString(),
          );

          // If found, return the full [id, name] array, otherwise return just the id
          return match || id;
        });

        if (JSON.stringify(fullItems) !== JSON.stringify(selectedItem)) {
          setSelectedItem(fullItems);
          hasGroupByParams = true;
        }
      }

      if (urlGroupApplied === 'true' && !groupApplied) {
        setGroupApplied(true);
        hasGroupByParams = true;
      }

      // Check if we have cached values for the current groupBy
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      const hasCachedValues =
        urlGroupBy && cachedValues[urlGroupBy] && Array.isArray(cachedValues[urlGroupBy]);

      // If we have cached values, use them for a faster initial render
      if (hasCachedValues) {
        setAvailableValuesGroupBy(cachedValues[urlGroupBy]);
      }

      // Check if we need to fetch group options
      if (hasGroupByParams && urlGroupBy) {
        // Pre-fetch the group options if needed
        let sortSelection = {};
        if (urlFilter && sortingArray1.includes(urlFilter)) {
          sortSelection.state = urlFilter?.toString().toLowerCase();
        }
        if (urlReadyStatus && urlReadyStatus !== 'Ready Status') {
          sortSelection.ready_status = urlReadyStatus?.toString().toLowerCase().replaceAll(' ', '_');
        }

        const params = {
          related_to: GROUP_BY.ORDER,
          field_name: urlGroupBy,
          search: urlSearchTerm
            ? {
              name: urlSearchTerm,
              origin: urlSearchTerm,
              partner_ref: urlSearchTerm,
              'move_ids.future_lot': urlSearchTerm,
              finished_products: urlSearchTerm,
              physical_container_no: urlSearchTerm,
              lot_color: searchTerm,
              rib_color: searchTerm
            }
            : {},
          sort: Object.keys(sortSelection).length > 0 ? { selection: sortSelection } : {},
        };

        // Fetch group by options first
        fetchGroupBY(params);
      }

      // Always fetch data on initial load to ensure all filters are applied
      if (!hasChanges) {
        getProductionList();
      }

      // Mark initial load as complete
      setInitialLoadComplete(true);
      isInitialLoad.current = false;
    }
  }, [availableValuesGroupBy, fetchGroupBY, getProductionList, groupApplied, groupBy, pageNumber, readyStatus, searchParams, searchTerm, selectedItem, sort1, sortingArray1]);

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'search', [search])) return;
    if (search) {
      setPageNumber(1); // Reset page number on new search
      getProductionList({ page_number: 1 }); // Don't use cache for new searches

      // Also refresh the group by options if needed
      if (initialLoadComplete && groupArray.includes(groupBy)) {
        refreshGroupByOptions(true); // Include filters since we're adding a search
      }
    }
  }, [search, getProductionList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions]);

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'pageNumber', [pageNumber])) return;
    if (!isInitialLoad.current) {
      const currentUrlPage = searchParams.get('page');
      if (currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
        getProductionList({}); // Use cache for pagination
      }
    }
  }, [pageNumber, getProductionList, searchParams]);

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'sort1_readyStatus_sortingArray1', [sort1, readyStatus, sortingArray1])) return;
    if (!isInitialLoad.current) {
      // Check if sort1 has changed from its initial value and sortingArray1 is populated
      const initialSort1 = searchParams.get('filter') || PRODUCTION_COLUMN[12].name;
      const initialReadyStatus = searchParams.get('readyStatus') || 'Ready Status';

      // Only trigger a new API call if the value has actually changed and sorting array is populated
      if ((sort1 !== initialSort1 && sortingArray1.length > 0) || readyStatus !== initialReadyStatus) {
        setPageNumber(1); // Reset page number on sorting change
        getProductionList({ page_number: 1 }); // Don't use cache for new sorts

        // Also refresh the group by options if needed
        if (groupArray.includes(groupBy)) {
          // Check if we're removing filters (going back to defaults)
          const includeFilters = sort1 !== PRODUCTION_COLUMN[12].name || readyStatus !== 'Ready Status';
          refreshGroupByOptions(includeFilters);
        }
      }
    }
  }, [sort1, readyStatus, sortingArray1, getProductionList, groupArray, groupBy, refreshGroupByOptions, searchParams]);

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'readyStatus', [readyStatus])) return;
    if (!isInitialLoad.current) {
      const initialReadyStatus = searchParams.get('readyStatus') || 'Ready Status';

      if (readyStatus !== initialReadyStatus) {
        setPageNumber(1);
        getProductionList({ page_number: 1 });
      }
    }
  }, [readyStatus, getProductionList, searchParams]);

  const resetFilter = () => {
    setSort1(PRODUCTION_COLUMN[12].name);
    setReadyStatus('Ready Status');
    setPageNumber(1);

    // Remove filter from URL but keep search and groupBy if present
    updateUrlParams({
      search: searchTerm,
      page: '1',
      groupBy: groupApplied ? groupBy : '',
      groupItems: groupApplied && selectedItem.length ? selectedItem : [],
      groupApplied: groupApplied,
    });

    getProductionList({ page_number: 1 }); // Don't use cache for filter reset

    // Refresh groupBy options without filters when filters are cleared
    if (groupArray.includes(groupBy)) {
      refreshGroupByOptions(false);
    }
  };

  const handleBulkManufacturing = () => {
    if (bulkMode) {
      setBulkMode(0);
      setShowRightTable(0);
      setFullProductData([]);
      setActiveRequests(0);
      setContainerNo('');
      setSelectionMode("page");
      setGlobalSelectAll(false);
      setExcludedIds(new Set());
      bulkRequestSeqRef.current += 1;
    } else {
      setBulkMode(1);
    }
  };

  const addBulkMO = ({ id }) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setExcludedIds(next);
    }
    //  else {
    const isSelected = moList.some((mo) => mo.id === id);

    if (isSelected) {
      setMoList((prev) => {
        const updatedList = prev.filter((mo) => mo.id !== id);
        if (updatedList.length === 0) {
          setShowRightTable(false);
          setFullProductData([]);
        }
        return updatedList;
      });

      setFullProductData((prevData) => {
        const filteredData = prevData.filter((item) => item.mo_id !== id);
        // Also clean up updatedMoves for moves that belong to unchecked records
        const remainingMoveIds = new Set(
          filteredData.map((item) => {
            const moveId = item.move_id;
            return moveId != null ? moveId.toString() : null;
          }).filter((id) => id != null)
        );
        setUpdatedMoves((prevMoves) => {
          const filteredMoves = {};
          Object.keys(prevMoves).forEach((moveId) => {
            const normalizedMoveId = moveId != null ? moveId.toString() : null;
            if (normalizedMoveId && remainingMoveIds.has(normalizedMoveId)) {
              filteredMoves[moveId] = prevMoves[moveId];
            }
          });
          return filteredMoves;
        });
        return filteredData;
      });
    } else {
      // Checking
      setShowRightTable(true);
      setMoList((prev) => [{ id }, ...prev]);
      setActiveRequests((prev) => prev + 1);

      // Mark as in progress (disable checkbox)
      setInProgressPickings((prev) => ({
        ...prev,
        [id]: true,
      }));

      vendorReceipt({ data: { receipt_ids: id, page_number: 1, manufacturing: true } })
        .then((response) => {
          setMoList((currentMoList) => {
            const stillExists = currentMoList.some((mo) => mo.id === id);
            if (!stillExists) return currentMoList;

            const alreadyInData = fullProductData.some((item) => item.mo_id === id);
            if (alreadyInData) return currentMoList;

            const records = response?.data?.records || [];
            const compiledData =
              records.length > 0 ? fabricManufacturingProductCompiler(records[0]) : [];

            const enrichedData = compiledData.map((item) => ({
              ...item,
              mo_id: id,
            }));

            setFullProductData((prevData) => [...prevData, ...enrichedData]);
            return currentMoList;
          });
        })
        .catch((error) => {
          console.error('Failed to fetch receipt data:', error);
        })
        .finally(() => {
          setActiveRequests((prev) => Math.max(prev - 1, 0));
          // Unmark in progress (enable checkbox)
          setInProgressPickings((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        });
    }
    // }
  };

  const checked = (id) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id);
    }
    return moList.some((mo) => mo.id === id);
  };

  const isInProgress = (id) => {
    return (
      !!inProgressPickings[id] ||
      inProgressIds.includes(id)
    );
  };

  const getPickingStatus = (id) => {
    return pickingStatus[id];
  };

  const handleExportClick = async (moListData) => {
    try {
      setIsExportCSVLoading(true);
      const picking_ids = moListData.map((mo) => mo.id);
      const response = await exportBulkManfacture({ data: { picking_ids } });

      if (response?.data) {
        setMoList([]);
        setSelectionMode("page");
        setBulkMode(0);
        setShowRightTable(0);
        setFullProductData([]);
        setIsExportCSVLoading(false);
        openProfileDocument(response.data, '_blank');
      } else {
        setIsExportCSVLoading(false);
        console.error("No download URL found in the response:", response);
      }
    } catch (error) {
      setIsExportCSVLoading(false);
      console.error("Export failed:", error);
    }
  };

  const handleExportTemplateClick = async () => {
    try {
      const response = await exportBulkManfactureTemplate({ data: {} });

      if (response?.data) {
        setMoList([]);
        setBulkMode(0);
        setShowRightTable(0);
        setFullProductData([]);
        openProfileDocument(response.data, '_blank');
      } else {
        console.error("No download URL found in the response:", response);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleFileSubmit = async (file) => {
    setImportFileLoading(true);

    try {
      // Read file as base64
      const base64File = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          try {
            const result = event.target.result.split(',')[1];
            resolve(result);
          } catch (err) {
            reject(new Error("Error parsing file"));
          }
        };

        reader.onerror = () => reject(new Error("File reading failed"));
        reader.readAsDataURL(file);
      });

      const response = await importBulkManfacture({
        data: { sheet_data: base64File, force_submit: false },
      });

      // Check if response is a warning
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        // Show warning popup and wait for confirmation
        setThresholdWarningData(response);
        setShowThresholdWarning(true);
        setPendingImportData({
          sheet_data: base64File,
          onComplete: async (editedValues) => {
            // After confirmation, import with force_submit=True
            try {
              const importResponse = await importBulkManfacture({
                data: { sheet_data: base64File, force_submit: true },
              });

              if (importResponse?.data) {
                const pickingIds = importResponse.data;

                // If edited values provided, update moves with edited quantities before validation
                if (editedValues && editedValues.length > 0) {
                  // Create move_ids array from edited values for save_as_draft API
                  const move_ids = editedValues.map(ev => ({
                    move_id: ev.moveId,
                    producing_quantity: ev.quantity,
                    damaged_quantity: 0  // Can be updated if needed
                  }));

                  // Update moves with edited quantities using save_as_draft API
                  try {
                    await saveAsDraftManufacturing({
                      data: {
                        move_ids: move_ids,
                        picking_ids: pickingIds,
                        container_number: false,
                        force_submit: true
                      }
                    });
                  } catch (updateError) {
                    console.error("Error updating moves with edited quantities:", updateError);
                    // Continue with validation even if update fails
                  }
                }

                getProductionList({});

                validateBulkManufacturing({ data: { picking_ids: pickingIds, manufacturing_via_import: true } })
                  .then((response) => {
                    popup("success", response?.message);
                    getProductionList({});
                  })
                  .catch((err) => {
                    console.error("Bulk validation failed:", err);
                    popup("error", "Validation failed for imported records.");
                  });
              }
              setMoImport(false);
            } catch (error) {
              console.error("Error during import after confirmation:", error);
              popup("error", "Failed to import manufacturing data.");
              setMoImport(false);
            }
          },
          onCancel: () => {
            // User cancelled - stop the process
            setMoImport(false);
          }
        });
        setImportFileLoading(false);
        return;
      }

      if (response?.data) {
        popup('success', "Manufacturing Started !");
        const pickingIds = response.data;

        getProductionList({});

        validateBulkManufacturing({ data: { picking_ids: pickingIds, manufacturing_via_import: true } })
          .then((response) => {
            popup("success", response?.message);
            getProductionList({});
          })
          .catch((err) => {
            console.error("Bulk validation failed:", err);
          });
      }

      setMoImport(false);

    } catch (error) {
      console.error("Error during import:", error);
      popup("error", error.message || "Something went wrong");
    } finally {
      setImportFileLoading(false);
    }
  };

  const handleSelectAllVisible = () => {
    const visibleAssignedIds = ProductionData
      .filter(row =>
        row.state === 'assigned' &&
        !inProgressPickings[row.id] &&
        !inProgressIds.includes(row.id)
      )
      .map(row => row.id);

    visibleAssignedIds.forEach(id => {
      if (!checked(id)) {
        addBulkMO({ id });
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = async () => {
    try {
      setActiveRequests((prev) => prev + 1);
      setMoList([]);
      setSelectionMode("all");
      setGlobalSelectAll(true);
      const clearedExcludedIds = new Set();
      setExcludedIds(clearedExcludedIds);

      const myRequestId = bulkRequestSeqRef.current;

      const shouldUseGroupBy = shouldApplyGroupBy();

      const sortSelection = {};
      if (sortingArray1.includes(sort1)) {
        sortSelection.state = sort1?.toString().toLowerCase();
      }
      if (readyStatus !== "Ready Status") {
        sortSelection.ready_status = readyStatus?.toString().toLowerCase().replaceAll(" ", "_");
      }

      const payload = {
        select_all: true,
        manufacturing: true,
        search: searchTerm
          ? {
            name: searchTerm,
            origin: searchTerm,
            partner_ref: searchTerm,
            "move_ids.future_lot": searchTerm,
            finished_products: searchTerm,
            physical_container_no: searchTerm,
          }
          : {},
        sort: {
          selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
        },
        groupby_field: shouldUseGroupBy ? groupBy : '',
        excluded_ids: Array.from(clearedExcludedIds),
        page_number: 1
      };

      if (shouldUseGroupBy && selectedItem.length) {
        if (groupBy.includes(',')) {
          const fields = groupBy.split(',');
          const groupbySpecifics = {};
          fields.forEach((field) => {
            groupbySpecifics[field.trim()] = selectedItem.map((item) =>
              Array.isArray(item) && item.length > 1
                ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
                : isNaN(Number(item)) ? item : Number(item)
            );
          });
          payload.groupby_specifics = groupbySpecifics;
        } else {
          payload.groupby_specifics = {
            [groupBy]: selectedItem.map((item) =>
              Array.isArray(item) && item.length > 1
                ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
                : isNaN(Number(item)) ? item : Number(item)
            ),
          };
        }
      } else {
        payload.groupby_specifics = {};
      }

      // Single API call for all
      const response = await vendorReceipt({ data: payload });

      if (myRequestId !== bulkRequestSeqRef.current) {
        setActiveRequests((prev) => Math.max(prev - 1, 0));
        return;
      }

      const records = response?.data?.records || [];
      if (records.length > 0) {
        setShowRightTable(true);

        setMoList((currentMoList) => {
          let updatedMoList = [...currentMoList];
          let newFullProductData = [...fullProductData];

          records.forEach((rec) => {
            const stillExists = updatedMoList.some((mo) => mo.id === rec.id);
            if (!stillExists) {
              updatedMoList.push({ id: rec.id });
            }

            const alreadyInData = newFullProductData.some(
              (item) => item.mo_id === rec.id
            );
            if (!alreadyInData) {
              const compiledData =
                rec && rec !== null
                  ? fabricManufacturingProductCompiler(rec)
                  : [];

              const enrichedData = compiledData.map((item) => ({
                ...item,
                mo_id: rec.id,
              }));

              newFullProductData = [...newFullProductData, ...enrichedData];
            }
          });

          setFullProductData(newFullProductData);
          return updatedMoList;
        });
      }
      setActiveRequests((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Failed to fetch all matching records:", error);
      setActiveRequests((prev) => Math.max(prev - 1, 0));
    }
  };


  const handleSaveAsDraft = async (picking_ids = [], container_number = false, force_submit = false, providedMoveIds = null) => {
    setIsSaveAsDraftLoading(true);

    // Use provided move_ids if available, otherwise get from updatedMoves state
    const move_ids = providedMoveIds || Object.entries(updatedMoves).map(([move_id, values]) => ({
      move_id,
      ...values,
    }));

    let hasWarning = false;
    try {
      const response = await saveAsDraftManufacturing({
        data: {
          move_ids,
          picking_ids,
          container_number,
          force_submit
        }
      });

      // Check if response is a warning
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        hasWarning = true;
        setIsSaveAsDraftLoading(false);
        // Don't clear updatedMoves when threshold warning - preserve for retry
        // Return warning object instead of true/false
        return { warning: true, data: response };
      }

      if (response) {
        popup('success', response?.message);
        setUpdatedMoves({});
        return true; // Success
      } else {
        return false;
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      return false;
    } finally {
      setIsSaveAsDraftLoading(false);
      // Only clear updatedMoves if no warning and not force_submit
      // Preserve updatedMoves when threshold warning occurs so user can retry without losing data
      if (!force_submit && !hasWarning) {
      setUpdatedMoves({});
    }
    }
  };

  const handleThresholdWarningConfirm = async (editedValues = []) => {
    setShowThresholdWarning(false);

    // Handle import manufacturing callback
    if (pendingImportData) {
      if (pendingImportData.onComplete) {
        await pendingImportData.onComplete(editedValues);
      }
      setPendingImportData(null);
      return;
    }

    // Handle save_as_draft callback - only proceed if callback exists (popup was shown from validate)
    if (!pendingSaveCallback) {
      console.warn("handleThresholdWarningConfirm called but no pendingSaveCallback exists");
      setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
      return;
    }

    // Update move_ids with edited quantities if provided (handle both produce and damage)
    let move_ids = [];
    if (editedValues && editedValues.length > 0 && pendingSaveCallback.move_ids) {
      // Update existing move_ids with edited quantities
      move_ids = pendingSaveCallback.move_ids.map(move => {
        // Convert both to numbers for comparison
        const moveIdNum = parseInt(move.move_id);
        const editedProduce = editedValues.find(ev => {
          const evMoveIdNum = parseInt(ev.moveId);
          return evMoveIdNum === moveIdNum && ev.type === 'produce';
        });
        const editedDamage = editedValues.find(ev => {
          const evMoveIdNum = parseInt(ev.moveId);
          return evMoveIdNum === moveIdNum && ev.type === 'damage';
        });

        const updatedMove = { ...move };
        if (editedProduce) {
          updatedMove.producing_quantity = parseFloat(editedProduce.quantity) || 0;
        }
        if (editedDamage) {
          updatedMove.damaged_quantity = parseFloat(editedDamage.quantity) || 0;
        }
        return updatedMove;
      });
    } else {
      move_ids = pendingSaveCallback.move_ids || [];
    }

    // Re-call save_as_draft with force_submit=true ONLY when user confirms on popup
    const { picking_ids, container_number } = pendingSaveCallback;
    const result = await handleSaveAsDraft(picking_ids, container_number, true, move_ids);

    // Execute the callback with the result
    if (pendingSaveCallback.onComplete) {
      pendingSaveCallback.onComplete(result === true);
    }

    // Clear callback after use
    setPendingSaveCallback(null);
  }

  const handleThresholdWarningCancel = () => {
    setShowThresholdWarning(false);
    setThresholdWarningData(null);

    // Handle import manufacturing cancellation
    if (pendingImportData && pendingImportData.onCancel) {
      pendingImportData.onCancel();
    }
    setPendingImportData(null);

    // Handle save_as_draft cancellation
    if (pendingSaveCallback && pendingSaveCallback.onCancel) {
      pendingSaveCallback.onCancel();
    }
    setPendingSaveCallback(null);
    setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
  }

  const handleValidate = async (moListData) => {
    setActiveValidateRequests((prev) => prev + 1);
    const picking_ids = moListData.map((mo) => mo.id);

    // ALWAYS clear any previous pending callbacks and popup state to ensure fresh start
    // This ensures each validate click starts fresh with force_submit=false
    if (pendingSaveCallback) {
      setPendingSaveCallback(null);
    }
    if (pendingImportData) {
      setPendingImportData(null);
    }
    // Close any existing popup to ensure fresh popup on next warning
    if (showThresholdWarning) {
      setShowThresholdWarning(false);
      setThresholdWarningData(null);
    }

    // IMPORTANT: Every validate click MUST start with force_submit=false
    // Popup will ALWAYS show if threshold exceeded
    // force_submit=true will ONLY be used when user clicks confirm on popup

    // Don't clear state here - preserve it in case user cancels threshold warning
    // State will be cleared only on successful completion

    // Filter updatedMoves to only include moves that belong to currently checked records
    // Get all move_ids from fullProductData that belong to the current moList
    const currentMoIds = new Set(picking_ids);
    const validMoveIds = new Set(
      fullProductData
        .filter((item) => currentMoIds.has(item.mo_id))
        .map((item) => {
          // Normalize move_id to string for consistent comparison
          const moveId = item.move_id;
          return moveId != null ? moveId.toString() : null;
        })
        .filter((id) => id != null)
    );

    // Get move_ids from updatedMoves, but only for moves that belong to checked records
    const move_ids = Object.entries(updatedMoves)
      .filter(([move_id]) => {
        // Check if this move_id belongs to a checked record
        // Normalize move_id to string for comparison
        const normalizedMoveId = move_id != null ? move_id.toString() : null;
        return normalizedMoveId && validMoveIds.has(normalizedMoveId);
      })
      .map(([move_id, values]) => ({
        move_id,
        ...values,
      }));

    // ALWAYS call with force_submit=false first - popup will show if threshold exceeded
    // This ensures popup ALWAYS appears when threshold is exceeded, never skip it
    const saveResult = await handleSaveAsDraft(picking_ids, containerNo, false);

    // ALWAYS check if threshold warning was returned - popup MUST show if threshold exceeded
    // Never proceed without showing popup when threshold is exceeded
    if (saveResult && saveResult.warning) {
      // Ensure we clear any previous callback before setting new one
      if (pendingSaveCallback) {
        setPendingSaveCallback(null);
      }

      // ALWAYS show warning popup and wait for user confirmation
      // User must click confirm to proceed with force_submit=true
      setThresholdWarningData(saveResult.data);
      setShowThresholdWarning(true);
      setPendingSaveCallback({
        picking_ids: picking_ids,
        container_number: containerNo,
        move_ids: move_ids, // Preserve move_ids for re-call after confirmation
        onComplete: async (saveSuccess) => {
          // Clear state only on successful completion
    setFullProductData([]);
    setBulkMode(0);
    setShowRightTable(0);
    setContainerNo('');
          if (saveSuccess) {
            // Proceed with validate API after successful save
            try {
              const response = await validateBulkManufacturing({ data: { picking_ids } });
              if (response) {
                popup('success', response?.message);
                getProductionList({});
              } else {
                popup('error', 'Server Error');
              }
            } catch (err) {
              console.error("Validation failed:", err);
              popup('error', err.message || 'Validation failed');
            } finally {
      setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
            }
          } else {
            setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
            popup('error', 'Failed to save draft quantities');
    }
        },
        onCancel: () => {
          // User cancelled - keep state intact (side window, selected records, quantities remain)
          // Just close the popup and decrement active requests
          setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
        }
      });
      return; // Wait for user confirmation
    }

    // No warning - proceed normally and clear state on success
    setContainerNo('');

    if (saveResult === true) {
    try {
      const response = await validateBulkManufacturing({ data: { picking_ids } });
      if (response) {
          // Clear state on successful validation
          setFullProductData([]);
          setBulkMode(0);
          setShowRightTable(0);
        popup('success', response?.message);
        getProductionList({});
      } else {
        popup('error', 'Server Error');
      }
    } catch (err) {
      console.error("Validation failed:", err);
        popup('error', err.message || 'Validation failed');
    } finally {
        setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
      }
    } else {
      setActiveValidateRequests((prev) => Math.max(prev - 1, 0));
    }
  };

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'searchTerm', [searchTerm])) return;
    if (!searchTerm && search) {
      setPageNumber(1);
      setSearch(false);

      updateUrlParams({
        filter: sortingArray1.includes(sort1) ? sort1 : '',
        readyStatus: readyStatus !== 'Ready Status' ? readyStatus : '',
        page: '1',
        groupBy: groupApplied ? groupBy : '',
        groupItems: groupApplied && selectedItem.length ? selectedItem : [],
        groupApplied: groupApplied,
      });

      getProductionList({ page_number: 1 });

      // If search is cleared and groupBy is applied, refresh the groupBy options
      // without any search filter
      if (groupArray.includes(groupBy)) {
        refreshGroupByOptions(false); // Don't include filters when search is removed
      }
    }
  }, [searchTerm, getProductionList, groupApplied, groupArray, groupBy, readyStatus, refreshGroupByOptions, search, selectedItem, sort1, sortingArray1, updateUrlParams]);


  return (
    <>
      <TitleBar title={[{ text: TITLES.MANUFACTURING, route: ROUTES.MANUFACTURING }]} />
      {/* <div className="d-flex align-items-center justify-content-end mb-3">
        <div className="me-2 color-label-container d-flex align-items-center justify-content-end">
          <div className="color-box light-green me-1"></div>
          <span>Done</span>
        </div>
        <div className="me-2 color-label-container d-flex align-items-center justify-content-end">
          <div className="color-box bright-orange me-1"></div>
          <span>Assigned</span>
        </div>
        <div className="me-2 color-label-container d-flex align-items-center justify-content-end">
          <div className="color-box bright-red me-1"></div>
          <span>Cancel</span>
        </div>
      </div> */}
      <div className="d-flex align-items-center justify-content-between gap-2 w-100">

        {/* Left side */}
        <div className="d-flex align-items-center gap-2 w-100">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={setSearch}
            onDefaultSearch={getProductionList}
          />

          <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
            <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
            {/* <DropDown dropdownMenu={readyStatusArray} value={readyStatus} setValue={setReadyStatus} /> */}

            {(sort1 !== PRODUCTION_COLUMN[12].name || readyStatus !== 'Ready Status') && (
              <Button
                clickHandler={resetFilter}
                className="blue-btn d-flex align-items-center justify-content-center px-3">
                {BUTTON.CLEAR_FILTERS}
              </Button>
            )}

            {isValidateLoading && (
              <>
                <span className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">Validating...</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Group By Controls */}
      {getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' && <div className="d-block align-items-center justify-content-start gap-3 w-100">
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={groupArray} value={groupBy} setValue={handleGroupByChange} />
          {groupArray.includes(groupBy) && (
            <InputWithSearch
              setAvailableItem={setAvailableValuesGroupBy}
              availableItem={availableValuesGroupBy}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
            />
          )}
          {groupArray.includes(groupBy) && (
            <Button
              clickHandler={removeGroupBy}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.REMOVE}
            </Button>
          )}
        </div>
      </div>}

      {Array.isArray(ProductionData) &&
        ProductionData.length > 0 &&
        ['fabric', 'dyeing'].includes(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE)) && (
          <div className="d-flex justify-content-between align-items-center mb-2 w-100">
            <div className="d-flex align-items-center left-side">
              {(bulkMode && !globalSelectAll) ? (
                <div className="d-flex align-items-center me-2">
                  <Button
                    clickHandler={handleSelectAllVisible}
                    className="blue-btn d-flex align-items-center justify-content-center px-3">
                    Select Page
                  </Button>
                </div>
              ) : ''}
              {!bulkMode && (
                <Button
                  clickHandler={() => setMoImport(true)}
                  disabled={isLoading}
                  className={`blue-btn d-flex align-items-center justify-content-center me-2 px-3 ${isLoading ? 'disabled' : ''}`}>
                  {BUTTON.IMPORT_CSV}
                </Button>
              )}
              {!bulkMode ? (
                <div className="d-flex align-items-center me-2">
                  <Button
                    clickHandler={handleExportTemplateClick}
                    className="blue-btn d-flex align-items-center justify-content-center me-2 px-3">
                    {BUTTON.EXPORT_TEMPLATE}
                  </Button>
                </div>
              ) : (
                <div />
              )}
              <Button
                clickHandler={handleBulkManufacturing}
                className={`${bulkMode ? 'white-btn' : 'blue-btn'} d-flex align-items-center justify-content-center me-2 px-3`}
              >
                {bulkMode ? BUTTON.CANCEL : BUTTON.BULK_MANUFACTURING}
              </Button>
              {(moList.length > 0 && !globalSelectAll) && (
                <>
                  <span className="text-muted" style={{ marginRight: "1rem" }}>{moList.length} {moList.length === 1 ? 'record' : 'records'} selected</span>
                  {!(moList.length === selectableRecords) && <span
                    onClick={handleSelectAllMatching}
                    className="text-primary cursor-pointer fw-semibold"
                    role="button"
                  >
                    Select All ({selectableRecords})
                  </span>}
                </>
              )}
              {selectionMode === 'all' && (
                <>
                  {(selectableRecords - excludedIds.size) > 0 && (
                    <span className="text-muted ms-2">
                      {selectableRecords - excludedIds.size}{' '}
                      {(selectableRecords - excludedIds.size) === 1 ? 'record' : 'records'} selected
                    </span>
                  )}
                  {excludedIds.size > 0 && <span
                    onClick={handleSelectAllMatching}
                    className="text-primary cursor-pointer fw-semibold"
                    role="button"
                  >
                    Select All ({selectableRecords})
                  </span>}
                </>
              )}
            </div >
            <div className="d-flex align-items-center right-side">
              {moList.length > 0 && (
                <>
                  <Input
                    type="text"
                    placeholder="Invoice No"
                    className="d-flex search-placeholder align-items-center justify-content-center container-input me-2"
                    value={containerNo}
                    onChange={(e) => setContainerNo(e.target.value)}
                  />
                  <Button
                    clickHandler={() => handleExportClick(moList)}
                    disabled={isExportCSVLoading}
                    className={`blue-btn d-flex align-items-center justify-content-center me-2 px-3 ${isExportCSVLoading ? 'disabled' : ''}`}>
                    {isExportCSVLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        />
                        <span className="ms-2">Exporting...</span>
                      </>
                    ) : (
                      BUTTON.EXPORT_CSV
                    )}
                  </Button>

                  <Button
                    clickHandler={() => handleSaveAsDraft(moList.map((item) => item.id), containerNo)}
                    className={`blue-btn d-flex align-items-center justify-content-center me-2 px-3 ${isSaveAsDraftLoading ? 'disabled' : ''}`}
                    disabled={isSaveAsDraftLoading}
                  >
                    {isSaveAsDraftLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        />
                        <span className="ms-2">Saving...</span>
                      </>
                    ) : (
                      BUTTON.SAVE_AS_DRAFT
                    )}
                  </Button>
                  <Button
                    clickHandler={() => handleValidate(moList)}
                    className="blue-btn d-flex align-items-center justify-content-center me-2 px-3"
                  >
                    {BUTTON.VALIDATE}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

      {Array.isArray(ProductionData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={ProductionData}
          colFilter={true}
          to={bulkMode ? '' : ROUTES.MANUFACTURING}
          compiler={{ use: true, tool: dispatchDataCompiler }}
          func={{ addBulkMO, checked, isInProgress, getPickingStatus }}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          showRightTable={showRightTable}
          fullProductData={fullProductData}
          rightTableLoading={rightTableLoading}
          isLoading={isLoading}
          setUpdatedMoves={setUpdatedMoves}
          rightTableColumn={rightTableColumn}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No records found</h4>
        </div>
      )}

      {moImport && (
        <PopUpBox openPopUpBox={moImport}>
          <ImportModalContent
            onClose={() => setMoImport(false)}
            onSubmit={handleFileSubmit}
            setImportFileLoading={setImportFileLoading}
            importFileLoading={importFileLoading}
          />
        </PopUpBox>
      )}
      {showThresholdWarning && thresholdWarningData && (
        <PopUpBox openPopUpBox={showThresholdWarning} modalWidth="50%">
          <ThresholdWarningPopup
            warningData={thresholdWarningData}
            onConfirm={handleThresholdWarningConfirm}
            onCancel={handleThresholdWarningCancel}
            allowEdit={!pendingImportData}
          />
        </PopUpBox>
      )}
    </>
  );
};
